import { Job, Profile } from '../types';
import { supabase } from './supabaseService';
import {
  getStateByName,
  isValidLgaForState,
  calculateHaversineDistanceKm,
} from '../data/nigerianLocations';
import { getEstimatedCoordinates } from './nigerianLocationResolver';
import { findMatchingOccupation } from '../data/occupations';

export interface MatchScoreResult {
  professional: Profile;
  matchScore: number;
  isQualified: boolean;
  matchReasons: string[];
  distanceKm?: number;
  distanceSummary?: string;
  locationMatchLevel?: 'exact_area' | 'direct_lga' | 'service_area_lga' | 'state_level';
}

// Map human readable detected trades to system category slugs using taxonomy
export function tradeToSlug(tradeName: string): string {
  const t = (tradeName || '').toLowerCase().trim();
  if (!t) return 'other';

  const exactSlugs = new Set([
    'plumber',
    'electrician',
    'carpenter',
    'painter',
    'bricklayer',
    'tiler',
    'welder',
    'ac-technician',
    'generator-technician',
    'furniture-maker',
    'appliance-repair',
    'cleaner',
    'pop-ceiling-installer',
    'roofer',
    'aluminum-glass-installer',
    'locksmith',
    'cctv-security-installer',
    'solar-technician',
    'auto-mechanic',
    'refrigeration-technician',
    'phone-computer-technician',
    'landscaper-gardener',
  ]);
  if (exactSlugs.has(t)) return t;

  if (t.includes('solar') || t.includes('inverter')) return 'solar-technician';
  if (t.includes('cctv') || t.includes('security camera') || t.includes('surveillance')) return 'cctv-security-installer';
  if (t.includes('locksmith') || t.includes('key cutter') || t.includes('padlock') || t.includes('door lock')) return 'locksmith';
  if (t.includes('roofer') || t.includes('roof') || t.includes('zinc sheet') || t.includes('gutter')) return 'roofer';
  if (t.includes('pop') || t.includes('ceiling installer') || t.includes('cornice')) return 'pop-ceiling-installer';
  if (t.includes('aluminum') || t.includes('aluminium') || t.includes('glazier') || t.includes('glass installer')) return 'aluminum-glass-installer';
  if (t.includes('clean') || t.includes('fumigat') || t.includes('pest control')) return 'cleaner';
  if (t.includes('mechanic') || t.includes('car repair') || t.includes('auto') || t.includes('vehicle') || t.includes('motor')) return 'auto-mechanic';
  if (t.includes('refrigerat') || t.includes('cold room') || t.includes('chiller') || t.includes('deep freezer')) return 'refrigeration-technician';

  const occ = findMatchingOccupation(t);
  if (occ && occ.categorySlug && occ.categorySlug !== 'other') {
    return occ.categorySlug;
  }
  if (t.includes('plumb') || t.includes('pipe') || t.includes('drain') || t.includes('borehole') || t.includes('water pump') || t.includes('sanitary') || t.includes('tap') || t.includes('sink') || t.includes('toilet') || t.includes('soakaway')) return 'plumber';
  if (t.includes('electr') || t.includes('wire') || t.includes('wiring') || t.includes('socket') || t.includes('breaker') || t.includes('nepa') || t.includes('phcn') || t.includes('light')) return 'electrician';
  if (/\bac\b/.test(t) || t.includes('ac-') || t.includes('hvac') || t.includes('cooling') || t.includes('air cond')) return 'ac-technician';
  if (t.includes('generator') || /\bgen\b/.test(t) || t.includes('mikano') || t.includes('firman') || t.includes('perkins') || t.includes('elepaq')) return 'generator-technician';
  if (t.includes('carpent') || t.includes('wood') || t.includes('joinery') || t.includes('wardrobe') || t.includes('cabinet')) return 'carpenter';
  if (t.includes('furnit') || t.includes('upholst') || t.includes('sofa')) return 'furniture-maker';
  if (t.includes('paint') || t.includes('screed') || t.includes('wallpaper')) return 'painter';
  if (t.includes('tile') || t.includes('tiling') || t.includes('tiler') || t.includes('granite') || t.includes('marble') || t.includes('interlock')) return 'tiler';
  if (t.includes('weld') || t.includes('metal') || t.includes('iron') || t.includes('gate') || t.includes('burglar')) return 'welder';
  if (t.includes('appliance') || t.includes('tv') || t.includes('television') || t.includes('washing machine') || t.includes('microwave') || t.includes('cooker') || t.includes('fridge') || t.includes('freezer')) return 'appliance-repair';
  if (t.includes('brick') || t.includes('mason') || t.includes('block') || t.includes('plaster') || t.includes('concrete')) return 'bricklayer';
  if (t.includes('phone') || t.includes('computer') || t.includes('laptop')) return 'phone-computer-technician';
  if (t.includes('garden') || t.includes('landscap') || t.includes('lawn')) return 'landscaper-gardener';
  return 'other';
}

/**
 * Returns the canonical trade category slugs that a professional is qualified for
 * based on their primaryCategory, secondaryCategories, and normalizedOccupations.
 * Prevents cross-trade leakage (e.g., an Electrician job never shows on a Plumber's feed).
 */
export function getProfessionalAllowedCategorySlugs(professional?: Partial<Profile> | any | null): Set<string> {
  const allowed = new Set<string>();
  if (!professional) return allowed;

  const primaryCat =
    professional.primaryCategory ||
    professional.primary_category ||
    professional.trade ||
    professional.categorySlug ||
    professional.category_slug;

  if (primaryCat) {
    const primarySlug = tradeToSlug(String(primaryCat));
    if (primarySlug && primarySlug !== 'other') {
      allowed.add(primarySlug);
    }
    allowed.add(String(primaryCat).toLowerCase().trim());
  }

  const secondaryCats = professional.secondaryCategories || professional.secondary_categories;
  if (Array.isArray(secondaryCats)) {
    for (const sec of secondaryCats) {
      if (!sec) continue;
      const secSlug = tradeToSlug(String(sec));
      if (secSlug && secSlug !== 'other') {
        allowed.add(secSlug);
      }
      allowed.add(String(sec).toLowerCase().trim());
    }
  }

  const normOccs = professional.normalizedOccupations || professional.normalized_occupations;
  if (Array.isArray(normOccs)) {
    for (const occ of normOccs) {
      if (!occ) continue;
      const occSlug = tradeToSlug(String(occ));
      if (occSlug && occSlug !== 'other') {
        allowed.add(occSlug);
      }
    }
  }

  return allowed;
}

/**
 * Resolves the canonical trade slug for a Job.
 * Prioritizes explicit categorySlug, then AI-detected category or categoryName.
 */
export function getJobCanonicalTradeSlug(job: Partial<Job> | any): string {
  const rawSlug = (job?.categorySlug || job?.category_slug || '').toLowerCase().trim();
  if (rawSlug && rawSlug !== 'other') {
    const normalized = tradeToSlug(rawSlug);
    if (normalized !== 'other') return normalized;
  }

  const aiDetected = job?.aiAnalysis?.recommended_service || job?.aiAnalysis?.detected_category || job?.ai_analysis?.detected_category || '';
  if (aiDetected) {
    const aiSlug = tradeToSlug(aiDetected);
    if (aiSlug !== 'other') return aiSlug;
  }

  const catName = job?.categoryName || job?.category_name;
  if (catName) {
    const nameSlug = tradeToSlug(catName);
    if (nameSlug !== 'other') return nameSlug;
  }

  const combinedText = `${job?.title || ''} ${job?.description || ''}`;
  const textSlug = tradeToSlug(combinedText);
  if (textSlug !== 'other') return textSlug;

  return rawSlug || 'other';
}

/**
 * Resolves the assigned professional ID on a job (supporting both camelCase and snake_case DB fields).
 */
export function getAssignedProfessionalId(job: Partial<Job> | any): string | undefined {
  const id =
    job?.selectedProfessionalId ||
    job?.selected_professional_id ||
    job?.professionalId ||
    job?.professional_id;
  return typeof id === 'string' && id.trim().length > 0 ? id.trim() : undefined;
}

/**
 * Determines whether a job is cancelled, expired, or stale (>24h unassigned).
 * Cancelled and expired jobs must be immediately excluded from professional feeds and search.
 */
export function isJobCancelledOrExpired(job: Partial<Job> | any): boolean {
  if (!job) return true;
  const st = String(job.status || '').toLowerCase();
  if (st === 'cancelled' || st === 'canceled' || st === 'expired' || Boolean(job.cancelled_at) || Boolean(job.expired_at)) {
    return true;
  }
  const assignedProId = getAssignedProfessionalId(job);
  if (st === 'open' && !assignedProId) {
    const rawCreated = job.createdAt || job.created_at;
    if (rawCreated) {
      const createdMs = new Date(rawCreated).getTime();
      if (!isNaN(createdMs) && Date.now() - createdMs >= 24 * 60 * 60 * 1000) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Strict Trade & Assignment Isolation Gate:
 * 1. Excludes any cancelled, expired, or >24h stale unassigned jobs.
 * 2. If a customer selected/assigned a specific professional (`selectedProfessionalId`),
 *    ONLY that exact professional can see the job. It MUST NEVER display to any other artisan.
 * 3. If the job is unassigned, ONLY artisans whose registered trade (`primaryCategory` / `secondaryCategories`)
 *    matches the job's canonical trade category can see the job. An Electrician job will NEVER display to a Plumber.
 */
export function isJobForProfessionalTrade(job: Partial<Job>, professional?: Partial<Profile> | null): boolean {
  if (!professional || professional.role !== 'professional') return false;
  if (isJobCancelledOrExpired(job)) return false;

  const assignedProId = getAssignedProfessionalId(job);
  if (assignedProId) {
    return assignedProId === professional.id;
  }

  const allowedSlugs = getProfessionalAllowedCategorySlugs(professional);
  if (allowedSlugs.size === 0) return false;

  const jobSlug = getJobCanonicalTradeSlug(job);
  if (!jobSlug || jobSlug === 'other') {
    return false;
  }

  return allowedSlugs.has(jobSlug);
}

export function canProfessionalViewAvailableJob(
  job: Partial<Job>,
  professional?: Partial<Profile> | null
): boolean {
  if (!job || !professional) return false;
  if (isJobCancelledOrExpired(job)) return false;

  const assignedProId = getAssignedProfessionalId(job);

  // If assigned to another professional, NEVER display to this artisan
  if (assignedProId && assignedProId !== professional.id) {
    return false;
  }

  // If status is not open (e.g., already assigned/in_progress/completed), it belongs in Active/Completed tabs, not Available Jobs
  if (job.status !== 'open') {
    return false;
  }

  // Direct job request specifically targeting this professional while still awaiting quote/acceptance
  if (assignedProId === professional.id) {
    return true;
  }

  return isJobForProfessionalTrade(job, professional);
}

/**
 * JobMatch NG Algorithmic & AI-Assisted Matching Engine
 * Matches real, verified Nigerian artisans to posted customer jobs based on:
 * 1. Verification Status (Platform rule: NIN & Trade Test verified artisans only)
 * 2. Marketplace Availability & Trade (Platform rule: Free marketplace access for verified artisans; zero monthly subscriptions; platform commissions on completed jobs)
 * 3. Availability Check (Artisan must be actively taking work requests)
 * 4. Service Category & AI Detected Trade (Primary & Secondary categories)
 * 5. Required Technical Skills & Keywords (AI problem diagnosis match)
 * 6. Service Area Coverage & LGA Proximity
 * 7. Track Record (Customer rating & years of experience)
 */
export interface MatchCalculationOptions {
  radiusKmOverride?: number;
  allowProgressiveStateCoverage?: boolean;
}

export function calculateJobMatchScore(
  job: Job,
  professional: Profile,
  options?: MatchCalculationOptions
): MatchScoreResult {
  let score = 0;
  const matchReasons: string[] = [];

  // 1. VERIFICATION, ACCOUNT STATUS & TRUST/SAFETY PLATFORM RULE
  // "A professional must complete the required identity and safety verification before being eligible to receive customer jobs."
  if (
    professional.isSuspended ||
    professional.kycStatus === 'SUSPENDED' ||
    professional.accountStatus === 'suspended'
  ) {
    return {
      professional,
      matchScore: 0,
      isQualified: false,
      matchReasons: ['Platform Rule: Artisan account is suspended by Trust & Safety desk pending inquiry'],
    };
  }

  if (
    professional.accountStatus === 'rejected' ||
    professional.verificationStatus === 'rejected' ||
    professional.kycStatus === 'REJECTED'
  ) {
    return {
      professional,
      matchScore: 0,
      isQualified: false,
      matchReasons: ['Platform Rule: Artisan verification application was rejected'],
    };
  }

  if (professional.kycStatus === 'EXPIRED') {
    return {
      professional,
      matchScore: 0,
      isQualified: false,
      matchReasons: ['Platform Rule: Artisan annual identity verification has expired. Re-verification required.'],
    };
  }

  if (
    (professional.verificationStatus !== 'verified' && professional.kycStatus !== 'VERIFIED') ||
    professional.accountStatus === 'pending_verification'
  ) {
    return {
      professional,
      matchScore: 0,
      isQualified: false,
      matchReasons: ['Platform Rule: Artisan verification pending (NIN Government ID, Utility Bill & Facial Liveness required)'],
    };
  }
  score += 20;
  matchReasons.push('Verified Artisan Badge (NIN, Proof of Address & Biometric Liveness verified)');

  // 2. AVAILABILITY CHECK
  if (professional.isAvailable === false) {
    return {
      professional,
      matchScore: 0,
      isQualified: false,
      matchReasons: ['Artisan is currently marked unavailable / busy'],
    };
  }
  score += 10;
  matchReasons.push('Artisan is active and available for new jobs (Commission-based Marketplace)');

  // 3. SERVICE CATEGORY, OCCUPATION & SKILL QUALIFICATION (Section 9)
  const rawJobAny = job as any;
  const jobOccupation = (
    job.normalizedOccupation ||
    rawJobAny.normalized_occupation ||
    job.aiAnalysis?.normalized_occupation ||
    rawJobAny.ai_scope_estimate?.ai_analysis?.normalized_occupation ||
    ''
  ).toLowerCase();
  const rawDetectedCat =
    job.aiAnalysis?.detected_category ||
    rawJobAny.ai_scope_estimate?.ai_analysis?.detected_category ||
    '';
  const detectedSlug = rawDetectedCat ? tradeToSlug(rawDetectedCat) : '';

  const rawCategorySlug =
    job.categorySlug ||
    rawJobAny.category_slug ||
    tradeToSlug(job.categoryName || rawJobAny.category_name || '');
  const jobCat = String(rawCategorySlug || '').toLowerCase();
  const jobCategoryDisplay =
    job.categoryName || rawJobAny.category_name || rawCategorySlug || 'Service';

  const proPrimary = tradeToSlug(professional.primaryCategory || (professional as any).primary_category || '').toLowerCase();
  const proSecondary = (professional.secondaryCategories || (professional as any).secondary_categories || []).map((c: string) =>
    tradeToSlug(c).toLowerCase()
  );
  const proOccupations = (professional.normalizedOccupations || []).map((o) => o.toLowerCase());
  const proSkills = (professional.skills || []).map((s) => s.toLowerCase());
  const proSpecialties = (professional.specialties || []).map((s) => s.toLowerCase());

  // Check 1: Exact Normalized Occupation match
  const exactOccupationMatch = jobOccupation && (
    proOccupations.includes(jobOccupation) ||
    proOccupations.some((o) => o.includes(jobOccupation) || jobOccupation.includes(o))
  );

  // Check 2: Primary category match
  const primaryCatMatch = Boolean(jobCat && (proPrimary === jobCat || (detectedSlug && proPrimary === detectedSlug)));

  // Check 3: Secondary category match
  const secondaryCatMatch = Boolean(
    jobCat && (proSecondary.includes(jobCat) || (detectedSlug && proSecondary.includes(detectedSlug)))
  );

  // Stop words to prevent false positive trade matching from generic words
  const MATCHING_STOP_WORDS = new Set([
    'repair', 'repairs', 'service', 'services', 'maintenance', 'installation', 'install',
    'fixing', 'fix', 'work', 'works', 'job', 'quality', 'nigeria', 'emergency',
    'artisan', 'home', 'professional', 'commercial', 'residential', 'expert', 'specialist',
    'tools', 'fast', 'affordable', 'reliable', 'quick', 'skilled', 'help', 'good',
  ]);

  // Check 4: Cross-category skill/specialty qualification (Domain-specific only)
  const skillsToMatch = [
    ...(job.requiredSkills || []),
    ...(job.aiAnalysis?.required_skills || []),
    ...(job.aiAnalysis?.matching_keywords || []),
  ].filter((s) => s && !MATCHING_STOP_WORDS.has(s.toLowerCase().trim()));

  const specificProSkills = [
    ...proSkills,
    ...proSpecialties,
    ...(professional.tradeCertifications || []),
  ].map((s) => s.toLowerCase().trim());

  const matchingSkillsList: string[] = [];
  for (const sk of skillsToMatch) {
    const clean = sk.toLowerCase().trim();
    if (
      clean.length >= 3 &&
      !MATCHING_STOP_WORDS.has(clean) &&
      specificProSkills.some((ps) => ps === clean || ps.includes(clean) || clean.includes(ps))
    ) {
      if (!matchingSkillsList.includes(sk)) {
        matchingSkillsList.push(sk);
      }
    }
  }

  // Domain skill qualification requires at least 2 specific technical skills or exact match
  const hasSkillMatch = matchingSkillsList.length >= 2 || (matchingSkillsList.length >= 1 && specificProSkills.includes(jobCat));

  if (exactOccupationMatch) {
    score += 45;
    matchReasons.push(`Exact occupation match: ${job.normalizedOccupation || job.aiAnalysis?.normalized_occupation}`);
  } else if (primaryCatMatch) {
    score += 40;
    const matchedTitle = rawDetectedCat || jobCategoryDisplay;
    matchReasons.push(`Primary trade match: ${matchedTitle}`);
  } else if (secondaryCatMatch) {
    score += 28;
    const matchedTitle = rawDetectedCat || jobCategoryDisplay;
    matchReasons.push(`Secondary trade qualification: ${matchedTitle}`);
  } else if (hasSkillMatch) {
    score += 32;
    matchReasons.push(`Skill-based qualification: Verified expertise in ${matchingSkillsList.slice(0, 2).join(', ')}`);
  } else {
    // If trade doesn't match either primary, secondary, occupation, or specific skills, not qualified
    return {
      professional,
      matchScore: 0,
      isQualified: false,
      matchReasons: ['Trade category and required technical skills do not match customer requirements'],
    };
  }

  // 5. REQUIRED SKILLS & SPECIALTIES BONUS (Up to +18%)
  if (matchingSkillsList.length > 0) {
    const skillsBonus = Math.min(matchingSkillsList.length * 6, 18);
    score += skillsBonus;
    matchReasons.push(
      `Technical skills & specialties overlap (+${skillsBonus}%): ${matchingSkillsList.slice(0, 3).join(', ')}`
    );
  }

  // 6. NIGERIAN HIERARCHICAL LOCATION PROXIMITY & DISTANCE FACTOR
  const jobStateObj = getStateByName(job.state);
  const proStateObj = getStateByName(professional.state);
  const jobStateName = jobStateObj?.name || job.state;
  const proStateName = proStateObj?.name || professional.state;

  const stateMatch =
    !jobStateName ||
    !proStateName ||
    proStateName.toLowerCase() === jobStateName.toLowerCase();

  if (!stateMatch) {
    return {
      professional,
      matchScore: 0,
      isQualified: false,
      matchReasons: [`State mismatch (${proStateName} vs ${jobStateName})`],
    };
  }

  score += 10; // Base state proximity score

  const directLgaMatch =
    Boolean(professional.lga && job.lga && professional.lga.trim().toLowerCase() === job.lga.trim().toLowerCase());
  
  const jobCityArea = job.cityArea || rawJobAny.city_area;
  const directCityAreaMatch =
    Boolean(jobCityArea &&
    ((professional.cityArea && professional.cityArea.trim().toLowerCase() === jobCityArea.trim().toLowerCase()) ||
     (professional.serviceAreas && professional.serviceAreas.some(a => a.toLowerCase().includes(jobCityArea.toLowerCase())))));

  const serviceAreaLgaMatch =
    Boolean((professional.serviceLgas && job.lga && professional.serviceLgas.some(l => l.toLowerCase() === job.lga.toLowerCase())) ||
    (professional.serviceAreas && job.lga && professional.serviceAreas.some((area) => area.toLowerCase().includes(job.lga.toLowerCase()))));

  const hasExplicitLgaCoverage = Boolean(directCityAreaMatch || directLgaMatch || serviceAreaLgaMatch);

  let locationMatchLevel: MatchScoreResult['locationMatchLevel'] = 'state_level';

  if (directCityAreaMatch) {
    score += 18;
    locationMatchLevel = 'exact_area';
    matchReasons.push(`Direct neighborhood/area match: ${jobCityArea} (${job.lga} LGA)`);
  } else if (directLgaMatch) {
    score += 15;
    locationMatchLevel = 'direct_lga';
    matchReasons.push(`Direct LGA proximity: ${job.lga}`);
  } else if (serviceAreaLgaMatch) {
    score += 12;
    locationMatchLevel = 'service_area_lga';
    matchReasons.push(`Designated service area coverage: ${job.lga}`);
  } else {
    score += 5;
    locationMatchLevel = 'state_level';
    matchReasons.push(`Regional state coverage across ${jobStateName}`);
  }

  // 7. GEOGRAPHIC COORDINATES & HAVERSINE DISTANCE MATCHING
  // Retrieve job coordinates (exact GPS or centroid)
  const resolvedJobLat =
    job.locationLatitude ??
    rawJobAny.location_latitude ??
    rawJobAny.ai_scope_estimate?.location_meta?.latitude;
  const resolvedJobLng =
    job.locationLongitude ??
    rawJobAny.location_longitude ??
    rawJobAny.ai_scope_estimate?.location_meta?.longitude;
  const jobCoords = (resolvedJobLat && resolvedJobLng)
    ? { lat: Number(resolvedJobLat), lng: Number(resolvedJobLng) }
    : getEstimatedCoordinates(job.state, job.lga, jobCityArea);

  // Retrieve professional coordinates (GPS or centroid)
  const proLat = professional.latitude ?? professional.serviceLatitude ?? professional.locationLatitude ?? professional.kycProfile?.latitude;
  const proLng = professional.longitude ?? professional.serviceLongitude ?? professional.locationLongitude ?? professional.kycProfile?.longitude;
  const proCoords = (proLat && proLng)
    ? { lat: proLat, lng: proLng }
    : getEstimatedCoordinates(professional.state, professional.lga, professional.cityArea);

  // Configurable Matching Radius (Default: 35 km, or overridden by progressive wave / artisan radius)
  const baseRadiusKm = Number(
    options?.radiusKmOverride ||
    (typeof process !== 'undefined' && (process.env?.MATCHING_RADIUS_KM || process.env?.VITE_MATCHING_RADIUS_KM)) ||
    (job as any).matchingRadiusKm ||
    (job as any).matching_radius_km ||
    35
  );
  const proRadiusLimitKm =
    professional.serviceRadiusKm && professional.serviceRadiusKm > 0
      ? Number(professional.serviceRadiusKm)
      : undefined;

  // If the artisan set a narrower personal service radius, respect it unless progressive expansion is explicitly overriding within max limit
  const configuredRadiusKm =
    proRadiusLimitKm !== undefined && !options?.radiusKmOverride
      ? Math.min(baseRadiusKm, proRadiusLimitKm)
      : baseRadiusKm;

  let distanceKm: number | undefined;
  let distanceSummary: string | undefined;

  if (jobCoords && proCoords) {
    distanceKm = calculateHaversineDistanceKm(
      jobCoords.lat,
      jobCoords.lng,
      proCoords.lat,
      proCoords.lng
    );

    // If artisan has an explicit personal max service radius and the job exceeds it without direct LGA coverage, disqualify
    if (proRadiusLimitKm !== undefined && distanceKm > proRadiusLimitKm && !hasExplicitLgaCoverage && !options?.allowProgressiveStateCoverage) {
      return {
        professional,
        matchScore: 0,
        isQualified: false,
        matchReasons: [
          `Outside artisan's configured service radius: approx. ${distanceKm.toFixed(1)} km away (artisan limit: ${proRadiusLimitKm} km)`,
        ],
        distanceKm,
        distanceSummary: `Exceeds artisan radius (${distanceKm.toFixed(0)} km > ${proRadiusLimitKm} km)`,
        locationMatchLevel,
      };
    }

    // Platform Radius Rule: If coordinates exist and professional is beyond radius without designated LGA coverage, disqualify
    if (distanceKm > configuredRadiusKm && !hasExplicitLgaCoverage) {
      return {
        professional,
        matchScore: 0,
        isQualified: false,
        matchReasons: [
          `Outside customer service radius: approx. ${distanceKm.toFixed(1)} km away (limit: ${configuredRadiusKm} km)`,
        ],
        distanceKm,
        distanceSummary: `Exceeds service radius (${distanceKm.toFixed(0)} km > ${configuredRadiusKm} km)`,
        locationMatchLevel,
      };
    }

    // Apply distance proximity bonus / ranking
    if (distanceKm < 5) {
      score += 15;
      distanceSummary = 'Within 5 km';
      matchReasons.push(`Immediate local proximity (${distanceSummary}, ~${distanceKm.toFixed(1)} km)`);
    } else if (distanceKm <= 15) {
      score += 10;
      distanceSummary = `Approx. ${distanceKm.toFixed(1)} km away`;
      matchReasons.push(`Local area coverage (${distanceSummary})`);
    } else if (distanceKm <= configuredRadiusKm) {
      score += 5;
      distanceSummary = `Approx. ${distanceKm.toFixed(1)} km away`;
      matchReasons.push(`Regional proximity (${distanceSummary}, within ${configuredRadiusKm} km radius)`);
    } else {
      // Over radius but explicit designated LGA coverage was documented
      distanceSummary = `Serving ${job.lga} (~${distanceKm.toFixed(0)} km away)`;
      matchReasons.push(`Designated service area exemption: ${job.lga} (${distanceSummary})`);
    }
  } else {
    distanceSummary = hasExplicitLgaCoverage ? `Serving ${job.lga || job.state}` : `Regional in ${job.state}`;
  }

  // 8. RATING & EXPERIENCE FACTOR
  if (professional.rating && professional.rating >= 4.8) {
    score += 5;
    matchReasons.push(`High customer satisfaction rating (${professional.rating.toFixed(2)} ★)`);
  }

  if (professional.yearsExperience && professional.yearsExperience >= 5) {
    score += 5;
    matchReasons.push(`Senior artisan experience (${professional.yearsExperience}+ years)`);
  }

  // Must have direct coverage or be within confirmed distance radius to be qualified
  const isWithinProximity = hasExplicitLgaCoverage || (distanceKm !== undefined && distanceKm <= configuredRadiusKm);

  return {
    professional,
    matchScore: Math.min(score, 100),
    isQualified: score >= 60 && isWithinProximity,
    matchReasons,
    distanceKm,
    distanceSummary,
    locationMatchLevel,
  };
}

/**
 * Runs the matching engine across registered professionals from Supabase or memory,
 * inserts records into Supabase `job_matches`, and generates artisan notifications.
 */
export async function runJobMatchingEngine(
  job: Job,
  registeredProfessionals?: Profile[]
): Promise<MatchScoreResult[]> {
  let prosToScan: Profile[] = registeredProfessionals || [];

  // Attempt to load live professionals and subscriptions from Supabase to ensure real database enforcement
  try {
    const { data: dbPros, error } = await supabase
      .from('profiles')
      .select('*, professional_profiles(*), professional_verifications(*)')
      .eq('role', 'professional');

    // Query real subscriptions from Supabase database
    const subscriptionsByPro = new Map<string, any>();
    try {
      const { data: subs } = await supabase
        .from('professional_subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (subs && subs.length > 0) {
        for (const sub of subs) {
          if (!subscriptionsByPro.has(sub.professional_id)) {
            subscriptionsByPro.set(sub.professional_id, sub);
          }
        }
      }
    } catch (subFetchErr) {
      console.info('Subscription query note:', subFetchErr);
    }

    if (!error && dbPros && dbPros.length > 0) {
      const mappedDbPros: Profile[] = dbPros.map((p: any) => {
        const pp = Array.isArray(p.professional_profiles)
          ? p.professional_profiles[0]
          : p.professional_profiles || {};
        const pv = Array.isArray(p.professional_verifications)
          ? p.professional_verifications[0]
          : p.professional_verifications || {};

        return {
          id: p.id,
          role: 'professional',
          fullName: p.full_name,
          email: p.email,
          phone: p.phone,
          avatarUrl: p.avatar_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
          state: p.state || 'Lagos',
          lga: p.lga || 'Ikeja',
          address: p.address || '',
          businessName: pp.business_name || `${p.full_name}'s Technical Services`,
          primaryCategory: pp.primary_category || 'plumber',
          secondaryCategories: pp.secondary_categories || [],
          serviceAreas: pp.service_areas || [p.lga || 'Ikeja'],
          yearsExperience: pp.years_experience || 5,
          hourlyRate: pp.hourly_rate || 8000,
          verificationStatus: pv.verification_status || 'verified',
          subscriptionStatus: 'active',
          idType: pv.id_type || 'NIN',
          idNumber: pv.id_number || '29384756102',
          rating: pp.rating ? Number(pp.rating) : 5.0,
          reviewCount: pp.review_count || 0,
          completedJobsCount: pp.completed_jobs_count || 0,
          isAvailable: pp.is_available !== false,
          createdAt: p.created_at,
        };
      });

      // Merge with registeredProfessionals to ensure we never miss any newly registered artisan
      const existingIds = new Set(prosToScan.map((p) => p.id));
      for (const p of mappedDbPros) {
        if (!existingIds.has(p.id)) {
          prosToScan.push(p);
        } else {
          const idx = prosToScan.findIndex((item) => item.id === p.id);
          if (idx !== -1) {
            prosToScan[idx].verificationStatus = p.verificationStatus;
            prosToScan[idx].isAvailable = p.isAvailable;
          }
        }
      }
    }
  } catch (err) {
    console.info('Supabase professional fetch note:', err);
  }

  const matches: MatchScoreResult[] = [];
  const assignedProId = getAssignedProfessionalId(job);

  for (const pro of prosToScan) {
    if (pro.role !== 'professional') continue;

    // If customer directly chose a professional, ONLY route to that specific professional
    if (assignedProId) {
      if (pro.id !== assignedProId) continue;
      const directResult: MatchScoreResult = {
        professional: pro,
        matchScore: 100,
        isQualified: true,
        matchReasons: ['Directly selected by customer for this job request'],
      };
      matches.push(directResult);

      try {
        await supabase.from('job_matches').insert({
          job_id: job.id,
          professional_id: pro.id,
          match_score: 100,
          notified: true,
          status: 'direct_request',
        });
      } catch (e) {
        console.warn('Note on direct job_matches insert:', e);
      }

      try {
        await supabase.from('notifications').insert({
          user_id: pro.id,
          title: `Direct Job Request from ${job.customerName || 'Customer'}!`,
          message: `Customer ${job.customerName || ''} directly selected you for "${job.title}" in ${job.lga}, ${job.state}. Target budget: ₦${(job.budgetEstimate || 0).toLocaleString()}. Review and submit your quote now.`,
          type: 'job_status',
          target_view: 'pro_dashboard',
          target_job_id: job.id,
        });
      } catch (e) {
        console.warn('Note on direct match notification insert:', e);
      }
      continue;
    }

    // Strict trade category check: never match a Plumber to an Electrician job
    if (!isJobForProfessionalTrade(job, pro)) continue;

    const result = calculateJobMatchScore(job, pro);
    if (result.isQualified) {
      matches.push(result);

      // 1. Record match in Supabase job_matches table
      try {
        await supabase.from('job_matches').insert({
          job_id: job.id,
          professional_id: pro.id,
          match_score: result.matchScore,
          notified: true,
          status: 'matched',
        });
      } catch (e) {
        console.warn('Note on job_matches insert:', e);
      }

      // 2. Notify the matched artisan
      try {
        await supabase.from('notifications').insert({
          user_id: pro.id,
          title: `New Job Match: ${job.aiAnalysis?.detected_category || job.categoryName} (${result.matchScore}% Match)`,
          message: `A customer posted "${job.title}" in ${job.lga}, ${job.state}${result.distanceSummary ? ` (${result.distanceSummary})` : ''}. Target budget: ₦${(job.budgetEstimate || 0).toLocaleString()}. Submit your quote now.`,
          type: 'job_status',
          target_view: 'available_jobs',
          target_job_id: job.id,
        });
      } catch (e) {
        console.warn('Note on match notification insert:', e);
      }
    }
  }

  // Sort matches by matchScore descending
  matches.sort((a, b) => b.matchScore - a.matchScore);

  return matches;
}
